import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Instagram, Leaf, Menu, ShoppingBag, UserRound, X } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { openCookieSettings } from '../../utils/cookieConsent';

export function Layout({ children }) {
  const { getItemCount } = useCart();
  const { user, loading: authLoading } = useCustomerAuth();
  const itemCount = getItemCount();
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/' && (!location.hash || location.hash === '#/');
  const isAbout = location.pathname === '/about';
  const isContact = location.pathname === '/contact';
  const isShop = location.pathname.startsWith('/shop');
  const isCart = location.pathname.startsWith('/cart');
  const isAccountPage =
    location.pathname.startsWith('/bejelentkezes') || location.pathname.startsWith('/profil');
  const accountTargetPath = user ? '/profil' : '/bejelentkezes';
  const activeYellowClass = 'bg-[#E6BD4E] text-foreground';

  useEffect(() => {
    setMobileMenuOpen(false);
    setProductsOpen(false);
  }, [location.pathname, location.search, location.hash]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="bg-destructive/10 border-b border-destructive/30">
        <div className="container-zinga flex items-center justify-center py-2 text-xs text-destructive font-semibold tracking-wide">
          TESZT HONLAP – nincs valós működés
        </div>
      </div>
      <div className="bg-secondary/70 border-b border-border/60">
        <div className="container-zinga flex items-center justify-between py-2 text-xs text-foreground/70">
          <span>Ingyenes szállítás 15 000 Ft felett</span>
          <span className="hidden sm:inline"></span>
        </div>
      </div>

      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur border-b border-border/60">
        <div className="container-zinga flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/lumen-verde-favicon.png?v=6"
              alt="Lumen Verde ikon"
              className="w-10 h-10 object-contain"
            />
            <div>
              <p className="font-display text-lg font-semibold">Lumen Verde</p>
              <p className="text-xs text-foreground/60">Tudatos választás, egy{"\u00A0"}kattintással</p>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <NavLink
              to="/"
              end
              className={() =>
                `nav-link ${isHome ? activeYellowClass : ''} hover:bg-[#CFE8D8]`
              }
            >
              Főoldal
            </NavLink>
            <div
              className="relative"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <NavLink
                to="/shop"
                className={() =>
                  `nav-link hover:bg-[#CFE8D8] hover:text-foreground ${
                    isShop ? activeYellowClass : ''
                  } ${productsOpen && !isShop ? 'bg-[#CFE8D8] text-foreground' : ''}`
                }
                aria-haspopup="menu"
                onClick={() => setProductsOpen(false)}
              >
                Termékek
              </NavLink>
              <div
                className={`absolute left-0 top-full pt-3 transition-all duration-200 ${
                  productsOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'
                }`}
                role="menu"
              >
                <div className="w-56 rounded-2xl border border-border/80 bg-card/95 backdrop-blur shadow-soft">
                  <div className="p-2">
                    <Link
                      to="/shop?category=foods"
                      className="block px-4 py-2 rounded-xl text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-amber-100/80 transition-colors"
                      role="menuitem"
                      onClick={() => setProductsOpen(false)}
                    >
                      Élelmiszer
                    </Link>
                    <Link
                      to="/shop?category=household"
                      className="block px-4 py-2 rounded-xl text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-amber-100/80 transition-colors"
                      role="menuitem"
                      onClick={() => setProductsOpen(false)}
                    >
                      Háztartás
                    </Link>
                    <Link
                      to="/shop?category=bodycare"
                      className="block px-4 py-2 rounded-xl text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-amber-100/80 transition-colors"
                      role="menuitem"
                      onClick={() => setProductsOpen(false)}
                    >
                      Testápolás
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <NavLink
              to="/about"
              className={() =>
                `nav-link hover:bg-[#CFE8D8] ${isAbout ? activeYellowClass : ''}`
              }
            >
              Rólunk
            </NavLink>
            <NavLink
              to="/contact"
              className={() =>
                `nav-link hover:bg-[#CFE8D8] ${isContact ? activeYellowClass : ''}`
              }
            >
              Kapcsolat
            </NavLink>
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/shop"
              className="hidden lg:inline-flex items-center gap-2 text-sm font-medium text-foreground/70 rounded-full px-4 py-2 transition-colors hover:bg-[#AFCBE6] hover:text-foreground"
            >
              <Leaf className="w-4 h-4" />
              Új termékek
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-border bg-card hover:bg-amber-100/80 transition-colors"
              aria-label={mobileMenuOpen ? 'Menü bezárása' : 'Menü megnyitása'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link
              to={accountTargetPath}
              className={`relative inline-flex items-center justify-center w-10 h-10 rounded-xl border border-border bg-card hover:bg-amber-100/80 transition-colors ${
                isAccountPage ? '!bg-[#E6BD4E]' : ''
              }`}
              aria-label={user ? 'Profil' : 'Bejelentkezés'}
            >
              <UserRound className="w-5 h-5" />
              {!authLoading && user ? (
                <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-emerald-600 border-2 border-background" />
              ) : null}
            </Link>
            <Link
              to="/cart"
              className={`relative inline-flex items-center justify-center w-10 h-10 rounded-xl border border-border bg-card hover:bg-amber-100/80 transition-colors ${
                isCart ? '!bg-[#E6BD4E]' : ''
              }`}
              aria-label="Kosár"
            >
              <ShoppingBag className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
          </div>
        </div>
        {mobileMenuOpen ? (
          <nav className="md:hidden border-t border-border/60 bg-background/95 backdrop-blur">
            <div className="container-zinga py-3 flex flex-col gap-2">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `nav-link w-full ${isActive ? activeYellowClass : 'hover:bg-[#CFE8D8]'}`
                }
              >
                Főoldal
              </NavLink>
              <NavLink
                to="/shop"
                className={({ isActive }) =>
                  `nav-link w-full ${isActive ? activeYellowClass : 'hover:bg-[#CFE8D8]'}`
                }
              >
                Termékek
              </NavLink>
              <Link to="/shop?category=foods" className="nav-link w-full hover:bg-[#CFE8D8]">
                Élelmiszer
              </Link>
              <Link to="/shop?category=household" className="nav-link w-full hover:bg-[#CFE8D8]">
                Háztartás
              </Link>
              <Link to="/shop?category=bodycare" className="nav-link w-full hover:bg-[#CFE8D8]">
                Testápolás
              </Link>
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `nav-link w-full ${isActive ? activeYellowClass : 'hover:bg-[#CFE8D8]'}`
                }
              >
                Rólunk
              </NavLink>
              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  `nav-link w-full ${isActive ? activeYellowClass : 'hover:bg-[#CFE8D8]'}`
                }
              >
                Kapcsolat
              </NavLink>
            </div>
          </nav>
        ) : null}
      </header>

      <main>{children}</main>

      <footer id="contact" className="border-t border-[#0B2F22] bg-[#0F3D2C] text-[#F1E6D3]">
        <div className="container-zinga py-12">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="inline-flex h-24 w-24 items-center justify-center rounded-2xl bg-[#F7F0DF] p-1">
                  <img
                    src="/lumen-verde-favicon.png?v=6"
                    alt="Lumen Verde ikon"
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div>
                  <p className="font-display text-2xl font-semibold leading-none text-[#F1E6D3]">
                    Lumen Verde
                  </p>
                  <p className="text-sm text-[#E7D9C2] mt-2">
                    Tudatos választás, egy{"\u00A0"}kattintással
                  </p>
                </div>
              </div>
            </div>
            <div className="md:pl-8">
              <p className="font-display font-semibold mb-3">Gyors linkek</p>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/shop" className="text-[#E7D9C2] hover:text-white">Termékek</Link>
                <Link to="/cart" className="text-[#E7D9C2] hover:text-white">Kosár</Link>
              </div>
            </div>
            <div>
              <p className="font-display font-semibold mb-3">Jogi dokumentumok</p>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/terms" className="text-[#E7D9C2] hover:text-white">ÁSZF</Link>
                <Link to="/privacy" className="text-[#E7D9C2] hover:text-white">Adatvédelmi tájékoztató</Link>
                <Link to="/elallasi-jog" className="text-[#E7D9C2] hover:text-white">Elállási jog</Link>
              </div>
            </div>
            <div>
              <p className="font-display font-semibold mb-3">Kapcsolat</p>
              <div className="text-sm text-[#E7D9C2] space-y-2">
                <p>info@lumenverde.hu</p>
                <p>Budapest, Magyarország</p>
                <a
                  href="https://www.instagram.com/lumen.verde/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#E7D9C2] hover:text-white transition-colors"
                  aria-label="Lumen Verde Instagram"
                >
                  <Instagram className="h-4 w-4" />
                  <span>lumen.verde</span>
                </a>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-[#154532] text-xs text-[#E7D9C2] flex flex-col sm:flex-row justify-between gap-2">
            <button
              type="button"
              onClick={openCookieSettings}
              className="w-fit rounded-md text-left font-semibold underline decoration-[#D7B55B] underline-offset-4 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F3C95D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F3D2C]"
            >
              Süti beállítások
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
